/**
 * Text and date helpers for titles, descriptions and alt text.
 *
 * Pure module (no `window`, no React): the SPA and the SEO server (plan W1)
 * must produce byte-identical strings, so nothing here depends on the runtime's
 * locale or time zone. Month and weekday names come from fixed English arrays
 * (`Sep`, never the `Sept` some ICU builds print), and calendar parts come from
 * `Intl.DateTimeFormat(...).formatToParts()` in the page locale's time zone.
 */
import { DEFAULT_LOCALE } from './locales';

// ─── Names and descriptions ────────────────────────────────────────────────

/** ™ ℠ ® © — NFKD would turn ™ into the letters "TM", so they go first. */
const TRADEMARK_SIGNS = /[\u{2122}\u{2120}\u{AE}\u{A9}]/gu;

/**
 * A keycap emoji (digit + U+FE0F + U+20E3) keeps its digit, so an "11.11"
 * spelled in keycaps still reads "11.11".
 */
const KEYCAP = /([#*0-9])\u{FE0F}?\u{20E3}/gu;

/**
 * A pictograph in emoji presentation (followed by U+FE0F) goes before NFKD,
 * which would turn ℹ️ into "i" and Ⓜ️ into "M". A plain Ⓜ is a styled letter
 * and folds to "M".
 */
const EMOJI_PRESENTATION = /\p{Extended_Pictographic}\u{FE0F}/gu;

/*
 * The two folding tables below are the backend's, entry for entry and in the
 * same shape (promostork_backend src/helpers/slug.ts, `SMALL_CAPITALS` and
 * `ENCLOSED_CHARACTERS`), so a name reads here what its slug says there.
 * src/seo/textFolding.parity.test.ts compares them with the backend's when
 * that checkout sits next to this app, and against a copied fixture list
 * everywhere else.
 */

/**
 * Small capitals: another "font" social posts use (`ꜱᴀʟᴇ ᴜᴘ ᴛᴏ 50%`). They
 * are letters of their own that NFKD leaves alone, so they would otherwise
 * stay small capitals in a title. Mapped to the plain lowercase letter, and
 * written as the capital (a small capital is a capital), as the backend's
 * `cleanHeadline` does. There is no small capital X; ǫ (used for q) is a real
 * letter that NFKD folds to o. Friends of the alphabet: the ligatures ᴁ ɶ,
 * barred/stroked ᴃ ᴌ ᵻ ᵾ, eth ᴆ, hooked ʛ, open ᴐ, reversed/inverted ᴎ ᴙ ʁ,
 * and the capital Ɪ. The superscript small capitals (ᶦ ᶫ ᶰ ᶸ) need no entry:
 * NFKD turns them into these first.
 */
export const SMALL_CAPITALS: Readonly<Record<string, string>> = Object.freeze({
  ᴀ: 'a',
  ʙ: 'b',
  ᴄ: 'c',
  ᴅ: 'd',
  ᴇ: 'e',
  ꜰ: 'f',
  ɢ: 'g',
  ʜ: 'h',
  ɪ: 'i',
  ᴊ: 'j',
  ᴋ: 'k',
  ʟ: 'l',
  ᴍ: 'm',
  ɴ: 'n',
  ᴏ: 'o',
  ᴘ: 'p',
  ꞯ: 'q',
  ʀ: 'r',
  ꜱ: 's',
  ᴛ: 't',
  ᴜ: 'u',
  ᴠ: 'v',
  ᴡ: 'w',
  ʏ: 'y',
  ᴢ: 'z',
  ᴁ: 'ae',
  ɶ: 'oe',
  ᴃ: 'b',
  ᴆ: 'd',
  ʛ: 'g',
  Ɪ: 'i',
  ᵻ: 'i',
  ᴌ: 'l',
  ᴎ: 'n',
  ᴐ: 'o',
  ʁ: 'r',
  ᴙ: 'r',
  ᵾ: 'u',
});
const SMALL_CAPITAL = new RegExp(`[${Object.keys(SMALL_CAPITALS).join('')}]`, 'gu');

/**
 * Enclosed letters and digits NFKD leaves alone (the circled Ⓐ ⓐ ① and the
 * squared 🄰 decompose by themselves): negative circled 🅐–🅩 and negative
 * squared 🅰–🆉 letters (capitals), and the dingbat circled digits ❶–❿,
 * ➀–➉, ➊–➓, ⓫–⓴, ⓵–⓾, ⓿, 🄋, 🄌 (their number: "❶❶.❶❶" is 11.11).
 * Folded before emoji are stripped — 🅰 🅱 🅾 🅿 are also emoji, and the rest
 * are symbols the emoji/symbol pass would drop — but after the
 * emoji-presentation pass, so 🅰️ (with U+FE0F) still goes as an emoji.
 */
export const ENCLOSED_CHARACTERS: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = {};
  const letters = (first: number) => {
    for (let i = 0; i < 26; i += 1) map[String.fromCodePoint(first + i)] = String.fromCharCode(65 + i);
  };
  const numbers = (first: number, from: number, to: number) => {
    for (let n = from; n <= to; n += 1) map[String.fromCodePoint(first + n - from)] = String(n);
  };
  letters(0x1f150); // 🅐–🅩 negative circled
  letters(0x1f170); // 🅰–🆉 negative squared
  numbers(0x2776, 1, 10); // ❶–❿ dingbat negative circled
  numbers(0x2780, 1, 10); // ➀–➉ dingbat circled sans-serif
  numbers(0x278a, 1, 10); // ➊–➓ dingbat negative circled sans-serif
  numbers(0x24eb, 11, 20); // ⓫–⓴ negative circled
  numbers(0x24f5, 1, 10); // ⓵–⓾ double circled
  map['\u{24FF}'] = '0'; // ⓿ negative circled zero
  map['\u{1F10B}'] = '0'; // 🄋 dingbat circled sans-serif zero
  map['\u{1F10C}'] = '0'; // 🄌 dingbat negative circled sans-serif zero
  return Object.freeze(map);
})();
const ENCLOSED_CHARACTER = new RegExp(`[${Object.keys(ENCLOSED_CHARACTERS).join('')}]`, 'gu');

/**
 * Emoji, including ZWJ sequences, skin tones, flags, variation selectors and
 * tag characters. A ZWJ is removed only next to a pictograph: Urdu text can
 * carry joiners of its own.
 */
const EMOJI =
  /\u{200D}?(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|[\u{FE0E}\u{FE0F}\u{20E3}]|[\u{E0020}-\u{E007F}])\u{200D}?/gu;

/** Decorative symbols (★ ✓ ♛ ❖ …). Currency (`\p{Sc}`) and `%` are kept. */
const OTHER_SYMBOLS = /\p{So}/gu;

/** Invisible characters that only get in the way: ZW space, word joiner, BOM, soft hyphen. */
const INVISIBLE = /[\u{200B}\u{2060}\u{FEFF}\u{AD}]/gu;

/**
 * Folds the styled Unicode that social posts use into plain text:
 * mathematical bold/italic/sans letters and digits (`𝗖𝗹𝗲𝗮𝗿𝗮𝗻𝗰𝗲` → `Clearance`),
 * fullwidth and circled forms, ligatures, enclosed letters and dingbat digits
 * (`🆂🅰🅻🅴` → `SALE`, `❶❶.❶❶` → `11.11`), small capitals (`ꜱᴀʟᴇ` → `SALE`);
 * strips emoji and decorative symbols. NFKD does most of the folding and NFC
 * recomposes accents afterwards, so `Élan` stays `Élan` and Urdu keeps its
 * marks. The same passes, in the same order, as the backend's `cleanHeadline`
 * (promostork_backend src/helpers/slug.ts).
 */
function foldText(value: string): string {
  const unemoji = value.replace(KEYCAP, '$1').replace(EMOJI_PRESENTATION, ' ');
  return unemoji
    .replace(ENCLOSED_CHARACTER, (ch) => ENCLOSED_CHARACTERS[ch] ?? ch)
    .replace(TRADEMARK_SIGNS, ' ')
    .normalize('NFKD')
    .replace(EMOJI, ' ')
    .replace(OTHER_SYMBOLS, ' ')
    .normalize('NFC')
    .replace(SMALL_CAPITAL, (letter) => (SMALL_CAPITALS[letter] ?? letter).toUpperCase())
    .replace(INVISIBLE, '')
    .replace(/[\p{Cc}]/gu, (c) => (c === '\n' ? '\n' : ' '));
}

/** "Sale!!!" → "Sale!", "--" → "-", ".." → ".", "..." → "…". */
function collapsePunctuation(value: string): string {
  return value
    .replace(/\.{3,}/g, '…')
    .replace(/\.{2}/g, '.')
    .replace(/([!?])[!?]+/g, '$1')
    .replace(/([,;:*~_=+|\\/#\-–—])\1+/g, '$1');
}

/** Spacing left behind by removed emoji: "Sale 🔥!" → "Sale !" → "Sale!". */
function tidySpacing(value: string): string {
  return value
    .replace(/[ \t]+([!?.,;:%)\]…])/g, '$1')
    .replace(/([([])[ \t]+/g, '$1')
    .replace(/\(\s*\)|\[\s*\]/g, '')
    .replace(/[ \t]{2,}/g, ' ');
}

/** URLs, with or without a scheme. */
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;

/** `URL_PATTERN` without `g`, so `.test()` keeps no `lastIndex` between lines. */
const HAS_URL = new RegExp(URL_PATTERN.source, 'i');

/**
 * What is left of a link-only caption line once its URLs are removed: emoji
 * and punctuation, at most one short label ending in ":" (one to three words),
 * then emoji and punctuation again. Words start with a letter, so the leading
 * run and the label never compete for the same characters, and the trailing
 * run sits inside the optional group: the regex runs in linear time. Captions
 * are written by brand and shop staff, and a repeated URL group here used to
 * backtrack exponentially on text like "http://http://… x".
 */
const LINK_LABEL_REMAINDER =
  /^[^\p{L}\p{N}]*(?:\p{L}[\p{L}\p{M}]*(?:[ \t]+\p{L}[\p{L}\p{M}]*){0,2}[ \t]*:[^\p{L}\p{N}]*)?$/u;

/**
 * A caption line that is nothing but a link, optionally behind a short label
 * and emoji: "🌐 Website: www.brand.pk", "📍 Store Locations: https://…".
 * Removing only the URL would leave a dangling "Website:", so the whole line
 * goes. A line with other words ("Shop at www.brand.pk now") is not matched;
 * only its URL is removed.
 */
function isLinkOnlyLine(line: string): boolean {
  return HAS_URL.test(line) && LINK_LABEL_REMAINDER.test(line.replace(URL_PATTERN, ' '));
}

function dropLinkOnlyLines(value: string): string {
  return value
    .split('\n')
    .filter((line) => !isLinkOnlyLine(line))
    .join('\n');
}

/**
 * `#Hashtags` (any script) that contain at least one letter or underscore,
 * so "#1" and a price like "#500" survive but "#11_11" does not. A `#` right
 * after a letter ("C#") is not a hashtag, and HTML entities (`&#39;`) are
 * never touched.
 */
const HASHTAG = /(^|[^\p{L}\p{M}\p{N}_&/])#(?=[\p{L}\p{M}\p{N}_]*[\p{L}_])[\p{L}\p{M}\p{N}_]+/gu;

/** Separators trimmed from the end of a name; a final "." is left to `trimTrailingPeriods`. */
const TRAILING_SEPARATORS = /[\s\-–—|•·*~_:,;/\\(…]+$/u;

/** Ends in an initial or a one- or two-letter abbreviation: "J.", "Maria.B.", "Hang Ten & Co.". */
const ABBREVIATION_END = /(?:^|[\s.])\p{L}{1,2}\.$/u;

/**
 * "Mega Sale." → "Mega Sale", but the period that closes an initial or a short
 * abbreviation belongs to the name and stays. A loop rather than a lookbehind:
 * Safari before 16.4 rejects lookbehind regexes at parse time, and Vite's
 * default build target is Safari 14.
 */
function trimTrailingPeriods(value: string): string {
  let text = value;
  while (text.endsWith('.') && !ABBREVIATION_END.test(text)) text = text.slice(0, -1);
  return text;
}

/**
 * A display name fit for a title, H1 or alt text: URLs and hashtags stripped
 * (names are often cut from social captions, master D-10), styled Unicode
 * folded, emoji and decorative symbols stripped, repeated punctuation
 * collapsed, whitespace collapsed, and separators trimmed from both ends.
 *
 * `𝗖𝗹𝗲𝗮𝗿𝗮𝗻𝗰𝗲 𝗦𝗮𝗹𝗲 🔥🔥` → `Clearance Sale`;
 * `Flat 50% Off #KhaadiSale #Lahore` → `Flat 50% Off`. Returns `''` for nothing usable.
 */
export function cleanName(value: string | null | undefined): string {
  if (!value) return '';
  // URLs go before folding (and before "//" is collapsed); hashtags after, so a
  // styled or fullwidth "＃" is caught too — the same order as cleanDescription.
  const text = foldText(value.replace(URL_PATTERN, ' ')).replace(HASHTAG, '$1');
  const tidy = tidySpacing(collapsePunctuation(text.replace(/\s+/g, ' ')))
    .replace(/^[\s\-–—|•·*~_:,;./\\!?]+/u, '')
    .replace(TRAILING_SEPARATORS, '');
  return trimTrailingPeriods(tidy).replace(TRAILING_SEPARATORS, '').trim();
}

/**
 * A brand's name for titles, H1s, breadcrumbs and structured data: the short
 * `seoName` staff set (backend SPB5; empty everywhere until they do), else the
 * name. Cleaned like any display name. Visible brand names on cards and logos
 * keep using `name` untouched.
 */
export function brandSeoName(entity: { name?: string | null; seoName?: string | null } | null | undefined): string {
  return cleanName(entity?.seoName) || cleanName(entity?.name);
}

// Legal forms that end a registered company name ("Bata Pakistan Limited",
// "Servis Industries (Pvt) Ltd."): never part of what shoppers call the brand.
const LEGAL_SUFFIX =
  /[\s,]+(?:\(?\s*pvt\.?\s*\)?\s*\.?\s*ltd\.?|\(?\s*private\s*\)?\s+limited|\(?\s*smc[-\s]?private\s*\)?\s+limited|limited|ltd\.?|llc|inc\.?)\s*$/iu;

/**
 * A brand's name for a TITLE only (never a visible label, never stored): the
 * name without a trailing legal form ("Bata Pakistan Limited" → "Bata
 * Pakistan") and without a parenthesis that repeats it ("Stylo (Stylo Pvt Ltd)"
 * → "Stylo"), so long registered names do not push the title past ~60
 * characters, and an all-lower-case name capitalised. A name that would be left
 * with less than two letters is kept as it is. Staff's `seoName` (`brandSeoName`) wins over this; it is the fix at
 * the source.
 */
export function brandTitleName(entity: { name?: string | null; seoName?: string | null } | null | undefined): string {
  const explicit = cleanName(entity?.seoName);
  if (explicit) return explicit;
  const name = cleanName(entity?.name);
  let short = name;
  const repeated = /^(.+?)\s*\(\s*(.+?)\)\s*$/u.exec(short);
  if (repeated && repeated[2].toLowerCase().startsWith(repeated[1].trim().toLowerCase())) {
    short = repeated[1].trim();
  }
  const stripped = short.replace(LEGAL_SUFFIX, '').trim();
  if ((stripped.match(/\p{L}/gu)?.length ?? 0) >= 2) short = stripped;
  // A name typed all in lower case ("imtiaz") is capitalised word by word; a name with any
  // capital in it (a deliberate "adidas Originals", "eBay") is left as written.
  if (short === short.toLowerCase() && short !== short.toUpperCase()) {
    short = short.replace(/(^|[\s\-/&(])(\p{Ll})/gu, (_m, lead: string, letter: string) => `${lead}${letter.toUpperCase()}`);
  }
  return short || name;
}

/**
 * `cleanName()` for visible labels (cards, slides, JSON-LD names), or the
 * trimmed original when cleaning leaves nothing (an all-emoji name), so a
 * label is never blank.
 */
export function displayName(value: string | null | undefined): string {
  return cleanName(value) || (value ?? '').trim();
}

export interface CleanDescriptionOptions {
  /** Maximum length in characters, ellipsis included. Default 160; `Infinity` to keep it all. */
  max?: number;
  /** Keep paragraph breaks (for visible text). Meta descriptions are one line. */
  keepLineBreaks?: boolean;
}

/**
 * A description fit for a meta tag (or, with `keepLineBreaks` and
 * `max: Infinity`, for the page): URLs and hashtags stripped (master D-10) —
 * a line that is only a link goes whole, label included ("Website: www.…"),
 * styled Unicode folded, emoji removed, whitespace collapsed, and clamped at a
 * word boundary to `max` characters (default 160).
 */
export function cleanDescription(
  value: string | null | undefined,
  { max = 160, keepLineBreaks = false }: CleanDescriptionOptions = {},
): string {
  if (!value) return '';
  let text = foldText(dropLinkOnlyLines(value).replace(URL_PATTERN, ' ')).replace(HASHTAG, '$1');
  text = tidySpacing(collapsePunctuation(text));
  text = keepLineBreaks
    ? text
        .split('\n')
        .map((line) => line.replace(/\s+/g, ' ').trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    : text.replace(/\s+/g, ' ').trim();
  text = text.replace(/^[\s\-–—|•·*~_:,;./\\]+/u, '');
  return clampText(text, max);
}

/**
 * Private-use code points: icon-font glyphs copied from store locator pages
 * along with the address (U+F015 is Font Awesome's "home" icon).
 */
const PRIVATE_USE = /\p{Co}/gu;

/**
 * A postal address (or phone number) as the page shows it, fit for a meta
 * description and a `PostalAddress`: URLs, emoji, decorative symbols and
 * icon-font glyphs removed, styled Unicode folded, one line with single
 * spaces, stray separators trimmed from both ends. Unlike `cleanDescription`
 * every `#` stays: "Shop #F14" and "Plot # 118-A" are unit numbers, and store
 * addresses carry no hashtags. Nothing else is rewritten, so the text still
 * matches the address on the page.
 */
export function cleanAddress(value: string | null | undefined): string {
  if (!value) return '';
  return foldText(value.replace(URL_PATTERN, ' ').replace(PRIVATE_USE, ' '))
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—|•·*~_:,;./\\]+/u, '')
    .replace(/[\s\-–—|•·*~_:,;/\\]+$/u, '');
}

const HAS_UPPER = /\p{Lu}/u;

/**
 * A city name as typed, except that an all-lowercase one ("mandi bahaud din",
 * common in imported store rows) gets a capital at the start of each word.
 * Anything with a capital already ("DHA Lahore", "D.G. Khan") is kept as is.
 * (Moved here from ./promotionRails for the store page's H1, web plan W0.10;
 * promotionRails re-exports it.)
 */
export function cityLabel(city: string | null | undefined): string {
  const name = cleanName(city);
  if (!name || HAS_UPPER.test(name)) return name;
  return name.replace(/(^|[\s\-(])(\p{Ll})/gu, (_, before: string, letter: string) => before + letter.toUpperCase());
}

/**
 * Cut `text` to at most `max` characters (code points, so an Urdu or emoji
 * character is never split), at a word boundary when one is near, with a
 * trailing `…`.
 */
export function clampText(text: string, max: number): string {
  if (!Number.isFinite(max) || max <= 0) return text;
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  let cut = chars.slice(0, max - 1).join('');
  const space = cut.lastIndexOf(' ');
  if (space >= Math.floor(max * 0.6)) cut = cut.slice(0, space);
  cut = cut.replace(/[\s,;:.(\[–—\-|/·•]+$/u, '');
  return `${cut}…`;
}

// ─── Dates ─────────────────────────────────────────────────────────────────

/** Fixed English month abbreviations — never `Sept`, whatever the runtime's ICU prints. */
export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Fixed English weekday abbreviations, Sunday first (`Date#getUTCDay` order). */
export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type DateInput = Date | string | number | null | undefined;

/**
 * A valid `Date` from an ISO string, epoch milliseconds (number or digit
 * string — Mongo timestamps serialise either way) or a `Date`; `null` for
 * anything empty or unparseable.
 */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null;
  let date: Date;
  if (value instanceof Date) date = value;
  else if (typeof value === 'number') date = new Date(value);
  else {
    const s = value.trim();
    if (!s) return null;
    // 10+ digits is epoch time; a bare "2026" must stay a (UTC) year.
    date = /^\d{10,}$/.test(s) ? new Date(Number(s)) : new Date(s);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Which end of a calendar day a bare `YYYY-MM-DD` stands for. */
export type DayEdge = 'start' | 'end';

/**
 * True for a bare calendar day, `"2026-09-04"`: no time and no zone. A plain
 * boolean, not a `value is string` predicate: `false` also covers strings (a
 * full ISO date-time), so a predicate would narrow them away in a caller's
 * `else` branch.
 */
export function isDateOnly(value: DateInput): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * The instant a period's start or end date stands for. A full date-time (ISO
 * with `Z` or an offset, epoch milliseconds, a `Date`) is exact and passes
 * through `toDate`. A bare calendar day (`"2026-09-04"`, as the Scout tool
 * wrote promotion dates before the backend normalised writes) is a day in
 * `timeZone`: 00:00 for a start and 23:59:59.999 for an end, the backend's
 * write rule (promostork_backend src/helpers/promotionDates.ts). `new Date()`
 * reads such a day as UTC midnight, which is 05:00 in Karachi: the offer would
 * start five hours late and end nineteen hours early. `null` for anything
 * empty, unparseable or not a real day ("2026-02-30").
 */
export function toBoundaryDate(
  value: DateInput,
  edge: DayEdge,
  timeZone: string = DEFAULT_LOCALE.timeZone,
): Date | null {
  if (typeof value !== 'string' || !isDateOnly(value)) return toDate(value);
  const day = parseLocalDay(value);
  if (!day) return null;
  return edge === 'start'
    ? zonedTimeToDate(day, timeZone)
    : zonedTimeToDate({ ...day, hour: 23, minute: 59, second: 59, ms: 999 }, timeZone);
}

export interface ZonedParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday */
  weekday: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let fmt = formatters.get(timeZone);
  if (!fmt) {
    // 'en-US' only fixes the digits (Latin) and the part types; the names we
    // print come from the fixed arrays above.
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, fmt);
  }
  return fmt;
}

/** Calendar and clock parts of an instant as seen in `timeZone`. */
export function zonedParts(date: Date, timeZone: string = DEFAULT_LOCALE.timeZone): ZonedParts {
  const parts: Record<string, number> = {};
  for (const part of formatterFor(timeZone).formatToParts(date)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  const { year, month, day } = parts;
  return {
    year,
    month,
    day,
    hour: parts.hour % 24,
    minute: parts.minute,
    second: parts.second,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** Minutes east of UTC that `timeZone` uses at `date` (`+300` for Asia/Karachi). */
export function zoneOffsetMinutes(date: Date, timeZone: string = DEFAULT_LOCALE.timeZone): number {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const wholeSecond = Math.floor(date.getTime() / 1000) * 1000;
  return Math.round((asUtc - wholeSecond) / 60_000);
}

/**
 * The instant at which the wall clock in `timeZone` shows the given local time.
 * Re-checks the offset at the result, so days that change offset (DST in other
 * locales) still land on the right instant.
 */
export function zonedTimeToDate(
  local: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number; ms?: number },
  timeZone: string = DEFAULT_LOCALE.timeZone,
): Date {
  const guess = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour ?? 0,
    local.minute ?? 0,
    local.second ?? 0,
    local.ms ?? 0,
  );
  const first = guess - zoneOffsetMinutes(new Date(guess), timeZone) * 60_000;
  const second = guess - zoneOffsetMinutes(new Date(first), timeZone) * 60_000;
  return new Date(second);
}

const pad = (n: number, width = 2) => String(Math.abs(n)).padStart(width, '0');

/**
 * ISO 8601 with the zone's offset and whole seconds, as robots
 * `unavailable_after` wants it: `2026-10-07T23:59:59+05:00`.
 */
export function toZonedIsoString(
  value: DateInput,
  timeZone: string = DEFAULT_LOCALE.timeZone,
): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  const offset = zoneOffsetMinutes(date, timeZone);
  const sign = offset < 0 ? '-' : '+';
  return (
    `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` +
    `${sign}${pad(Math.trunc(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`
  );
}

/** "Sep 2026" in the locale's time zone; `''` for no date. */
export function formatMonthYear(value: DateInput, timeZone: string = DEFAULT_LOCALE.timeZone): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  return `${MONTHS_SHORT[p.month - 1]} ${p.year}`;
}

/** "30 Sep" in the locale's time zone; `''` for no date. */
export function formatDayMonth(value: DateInput, timeZone: string = DEFAULT_LOCALE.timeZone): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** "30 Sep 2026" in the locale's time zone; `''` for no date. */
export function formatDayMonthYear(
  value: DateInput,
  timeZone: string = DEFAULT_LOCALE.timeZone,
): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}`;
}

// ─── Local calendar days (sale events) ─────────────────────────────────────

export interface LocalDay {
  year: number;
  month: number;
  day: number;
}

/** A `YYYY-MM-DD` calendar day (no time, no zone), or `null` if it isn't a real date. */
export function parseLocalDay(value: string | null | undefined): LocalDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? '');
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    return null;
  }
  return { year, month, day };
}

/** "Wed 11 Nov 2026" for a `YYYY-MM-DD` day (no time-zone maths: it is already local). */
export function formatLocalDay(value: string, { weekday = true }: { weekday?: boolean } = {}): string {
  const d = parseLocalDay(value);
  if (!d) return '';
  const text = `${d.day} ${MONTHS_SHORT[d.month - 1]} ${d.year}`;
  if (!weekday) return text;
  return `${WEEKDAYS_SHORT[new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay()]} ${text}`;
}

/**
 * A span of local days: "Wed 11 Nov 2026" for one day, "8 Feb – 9 Mar 2027"
 * across months, "8 – 9 Mar 2027" within a month, "28 Dec 2026 – 2 Jan 2027"
 * across years.
 */
export function formatLocalDayRange(start: string, end?: string | null): string {
  const s = parseLocalDay(start);
  const e = end ? parseLocalDay(end) : null;
  if (!s) return '';
  if (!e || (e.year === s.year && e.month === s.month && e.day === s.day)) return formatLocalDay(start);
  if (s.year !== e.year) return `${formatLocalDay(start, { weekday: false })} – ${formatLocalDay(end!, { weekday: false })}`;
  const tail = `${e.day} ${MONTHS_SHORT[e.month - 1]} ${e.year}`;
  return s.month === e.month
    ? `${s.day} – ${tail}`
    : `${s.day} ${MONTHS_SHORT[s.month - 1]} – ${tail}`;
}
